import { Box, CircularProgress, SxProps, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { mergeSxProps } from '~/utils/helpers/sxProps';

import { useSxStyles } from './useStyles';

interface CustomSxStyles {
  root: SxProps<Theme>;
  errorMessage: SxProps<Theme>;
  spinner: SxProps<Theme>;
}

export interface Props {
  isLoading: boolean;
  failedToLoad?: boolean;
  failedToLoadMessage?: string | React.ReactElement;
  noData?: boolean;
  noDataMessage?: string | React.ReactElement;
  spinnerSize?: number;
  fullHeight?: boolean;
  children: React.ReactNode;
  customSxStyles?: Partial<CustomSxStyles>;
}

export const AsyncContent: React.FC<Props> = (props) => {
  const { customSxStyles } = props;
  const sxStyles = useSxStyles();
  const { t } = useTranslation();
  const rootStyle = {
    ...sxStyles.root,
    ...(props.fullHeight && sxStyles.fullHeight),
  };

  const sxCustom: CustomSxStyles = {
    root: mergeSxProps(rootStyle, customSxStyles?.root),
    errorMessage: mergeSxProps(sxStyles.errorMessage, customSxStyles?.root),
    spinner: mergeSxProps(sxStyles.spinner, customSxStyles?.root),
  };
  if (props.isLoading) {
    return (
      <Box sx={sxCustom.root}>
        <CircularProgress size={props.spinnerSize} sx={sxCustom.spinner} />
      </Box>
    );
  }

  if (props.failedToLoad || props.noData) {
    return (
      <Box sx={sxCustom.root}>
        <Typography variant="body2" sx={sxCustom.errorMessage}>
          {props.failedToLoad
            ? props.failedToLoadMessage || t('infoNotAvailable')
            : props.noDataMessage || t('noDataAvailable')}
        </Typography>
      </Box>
    );
  }

  return <Box sx={sxCustom.root}>{props.children}</Box>;
};
